import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MsgReader, { FieldsData } from '@kenjiuno/msgreader'

import useLocaleFormattedDate from '@/hooks/useLocaleFormattedDate'
import { dataURLtoFile } from '@/helpers/general-helpers'
import { FlexRow } from '@/ui'

interface Props {
  filePath: string
  fileName: string
}

function parseHeaders(headers: string) {
  const parsedHeaders = {}
  if (!headers) {
    return parsedHeaders
  }
  const headerRegEx = /(.*): (.*)/g
  let m = null
  while ((m = headerRegEx.exec(headers))) {
    // todo: Pay attention! Header can be presented many times (e.g. Received). Handle it, if needed!
    parsedHeaders[m[1]] = m[2]
  }
  return parsedHeaders
}

function getMsgDate(
  rawHeaders: string,
  localeFormattedDate: ReturnType<typeof useLocaleFormattedDate>,
) {
  // Example for the Date header
  const headers = parseHeaders(rawHeaders)
  if (!headers['Date']) {
    return '-'
  }
  const date = new Date(headers['Date'])

  return localeFormattedDate(date, {
    intlOptions: {
      dateStyle: 'full',
      timeStyle: 'long',
    },
  })
}

const MsgPreview = ({ filePath, fileName }: Props) => {
  const { t } = useTranslation()

  const [fileData, setFileData] = useState<FieldsData>()
  const [breakComponent, setBreakComponent] = useState(false)
  const localeFormattedDate = useLocaleFormattedDate()

  useEffect(() => {
    if (!fileData) {
      const file = dataURLtoFile(filePath, fileName)

      const reader = new FileReader()

      reader.readAsArrayBuffer(file)

      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          const msgReader = new MsgReader(reader.result)
          const fileData = msgReader.getFileData()
          if (!('error' in fileData)) {
            setFileData(fileData)
          } else {
            setBreakComponent(true)
          }
        }
      }

      reader.onerror = () => {
        setBreakComponent(true)
      }
    }
  }, [fileData, filePath, fileName])

  if (breakComponent) {
    throw new Error('Failed to read data')
  }

  if (!fileData) {
    return null
  }

  return (
    <div style={{ padding: '1em' }}>
      <FlexRow flexDirection="column">
        <strong>{t('From')}</strong>
        <div>
          {fileData.senderName}{' '}
          {fileData.senderEmail && `[${fileData.senderSmtpAddress}]`}
        </div>
      </FlexRow>

      {fileData.recipients && (
        <FlexRow flexDirection="column">
          <strong>{t('To')}</strong>
          {fileData.recipients.map(({ name, smtpAddress }) => (
            <div key={smtpAddress}>
              {name} {smtpAddress && `[${smtpAddress}]`}
            </div>
          ))}
        </FlexRow>
      )}

      {fileData.headers && (
        <FlexRow flexDirection="column">
          <strong>{t('Date')}</strong>
          <div>{getMsgDate(fileData.headers, localeFormattedDate)}</div>
        </FlexRow>
      )}
      <FlexRow flexDirection="column">
        <strong>{t('Subject')}</strong>
        <div>{fileData.subject}</div>
      </FlexRow>
      <FlexRow flexDirection="column">
        <strong>{t('Body')}</strong>
        <div
          style={{
            whiteSpace: 'pre',
            textAlign: 'left',
          }}>
          {fileData.body}
        </div>
      </FlexRow>
      {fileData.attachments && (
        <FlexRow flexDirection="column">
          <strong>{t('Attachments')}</strong>
          {fileData.attachments.map(({ dataId, name }) => (
            <div key={dataId}>{name}</div>
          ))}
        </FlexRow>
      )}
    </div>
  )
}

export default MsgPreview
